import { Maybe, Maybeify } from '../../types/maybe';
import { CustomerStatutoryDiscountType } from '../services/customers/enums/customer-statutory-discount-type';
import { IContactInfo, IMobile, ITelephone } from './contact-info';
import { CurrencyCode } from './enum/currency-code';

export interface ICustomerStatistics {
  totalTransactions: number;
  totalRefunds: number;
  totalAmountSpent: number;
  lastTransactionTimestamp: Date | null;
  lastTransactionId: string | null;
  averageAmountSpentPerTransaction: number;
  currencyCode: CurrencyCode;
}

export interface ICustomerCategory {
  category: string;
  count: number;
}

export interface ICustomerProduct {
  product: string;
}

export interface interfaceICustomerTransaction {
  transactoinId: string;
  transactionTimestamp: string;
  transactionTime: string;
}

export interface ICustomerTop10BestSellers {
  products: Maybe<ICustomerProduct[]>;
}

export interface ICustomerTransactionCountsByCategories {
  categories: Maybe<ICustomerCategory[]>;
}

export interface ICustomerTransactionHistory {
  transactions: Maybe<interfaceICustomerTransaction[]>;
}

export class CustomerStatistics implements ICustomerStatistics {
  constructor(
    public readonly totalTransactions = 0,
    public readonly totalRefunds = 0,
    public readonly totalAmountSpent = 0,
    public readonly lastTransactionTimestamp: Date | null = null,
    public readonly lastTransactionId: string | null = null,
    public readonly averageAmountSpentPerTransaction = 0,
    public readonly currencyCode = CurrencyCode.PHP,
  ) {}
}

type ICustomerAddress = Maybeify<IContactInfo>;
export interface ICustomer
  extends
    ICustomerAddress,
    ICustomerStatistics,
    ICustomerTop10BestSellers,
    ICustomerTransactionCountsByCategories,
    ICustomerTransactionHistory {
  id: number;
  createdAt: Maybe<Date>;
  updatedAt: Maybe<Date>;
  storeId: number;
  taxId: Maybe<string>;
  statutoryDiscountType: CustomerStatutoryDiscountType;
  statutoryDiscountIdExpiresAt: Maybe<Date>;
  statutoryDiscountIdNumber: Maybe<string>;
  note: Maybe<string>;
  childName: Maybe<string>;
  childBirthDate: Maybe<Date>;
}

export type CustomerUpdateData = Pick<ICustomer, 'id'> &
  Partial<Omit<ICustomer, 'id' | 'createdAt' | 'updatedAt'>>;

export class Customer implements ICustomer {
  constructor(
    public readonly createdAt: Maybe<Date> = null,
    public readonly updatedAt: Maybe<Date> = null,
    public readonly storeId: number = 0,
    public readonly id: number = 0,
    public readonly firstName: Maybe<string> = null,
    public readonly lastName: Maybe<string> = null,
    public readonly company: Maybe<string> = null,
    public readonly mobile: Maybe<IMobile> = null,
    public readonly telephone: Maybe<ITelephone> = null,
    public readonly email: Maybe<string> = null,
    public readonly birthday: Maybe<Date> = null,
    public readonly taxId: Maybe<string> = null,
    public readonly statutoryDiscountType: CustomerStatutoryDiscountType = CustomerStatutoryDiscountType.NONE,
    public readonly statutoryDiscountIdNumber: Maybe<string> = null,
    public readonly statutoryDiscountIdExpiresAt: Maybe<Date> = null,
    public readonly note: Maybe<string> = null,

    // customer address
    public readonly zipCode: Maybe<string> = null,
    public readonly barangay: Maybe<string> = null,
    public readonly city: Maybe<string> = null,
    public readonly district: Maybe<string> = null,
    public readonly province: Maybe<string> = null,
    public readonly unitFloorRoomBuildingNo: Maybe<string> = null,
    public readonly buildingNameTower: Maybe<string> = null,
    public readonly lotBlockPhaseHouseNo: Maybe<string> = null,
    public readonly street: Maybe<string> = null,
    public readonly subdivisionVillageZone: Maybe<string> = null,

    // customer statistics
    public readonly totalTransactions: number = 0,
    public readonly totalRefunds: number = 0,
    public readonly totalAmountSpent: number = 0,
    public readonly lastTransactionTimestamp: Date | null = null,
    public readonly lastTransactionId: string | null = '',
    public readonly averageAmountSpentPerTransaction: number = 0,
    public readonly currencyCode: CurrencyCode = CurrencyCode.PHP,

    //details
    public readonly products: Maybe<ICustomerProduct[]> = null,
    public readonly categories: Maybe<ICustomerCategory[]> = null,
    public readonly transactions: Maybe<interfaceICustomerTransaction[]> = null,

    // solo parent fields
    public readonly childName: Maybe<string> = null,
    public readonly childBirthDate: Maybe<Date> = null,
  ) {}

  get fullName() {
    return `${this.firstName} ${this.lastName}`;
  }

  get childAge(): number | null {
    if (!this.childBirthDate) {
      return null;
    }
    
    const today = new Date();
    const birthDate = new Date(this.childBirthDate);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    // Adjust if the birthday hasn't occurred this year yet
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  }

  get address() {
    const addressArray = [
      this.unitFloorRoomBuildingNo,
      this.buildingNameTower,
      this.lotBlockPhaseHouseNo,
      this.street,
      this.subdivisionVillageZone,
      this.barangay,
      this.district,
      this.city,
      this.province,
      this.zipCode,
    ];

    const result = addressArray.filter((s) => s && s.trim() !== '').join(', ');
    return result || null;
  }

  static fromJSON(json: Partial<ICustomer>) {
    return new Customer(
      json.createdAt,
      json.updatedAt,
      json.storeId,
      json.id,
      json.firstName,
      json.lastName,
      json.company,
      json.mobile,
      json.telephone,
      json.email,
      json.birthday,
      json.taxId,
      json.statutoryDiscountType,
      json.statutoryDiscountIdNumber,
      json.statutoryDiscountIdExpiresAt,
      json.note,
      json.zipCode,
      json.province,
      json.city,
      json.district,
      json.barangay,
      json.unitFloorRoomBuildingNo,
      json.buildingNameTower,
      json.lotBlockPhaseHouseNo,
      json.street,
      json.subdivisionVillageZone,
      json.totalTransactions,
      json.totalRefunds,
      json.totalAmountSpent,
      json.lastTransactionTimestamp,
      json.lastTransactionId,
      json.averageAmountSpentPerTransaction,
      json.currencyCode,
      json.products,
      json.categories,
      json.transactions,
      json.childName,
      json.childBirthDate,
    );
  }
}
